import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase/client';

export interface CampaignPerformanceData {
    campagne: string;
    call_count: number;
    resolution_rate: number;
    satisfaction_rate: number;
    avg_duration: number;
    performance_rate: number;
    pain_points_rate: number;
    conversion_rate: number;
    reiteration_rate: number;
}

interface UseCampaignsPerformanceParams {
    projectId: number | null;
    startDate?: Date | null;
    endDate?: Date | null;
    enabled?: boolean;
}

export const useCampaignsPerformance = ({
    projectId,
    startDate,
    endDate,
    enabled = true,
}: UseCampaignsPerformanceParams) => {
    return useQuery({
        queryKey: ['campaigns-performance', projectId, startDate, endDate],
        queryFn: async () => {
            if (!projectId) {
                throw new Error('Project ID is required');
            }

            const { data, error } = await supabase.rpc('get_campaigns_performance', {
                p_project_id: projectId,
                p_start_date: startDate?.toISOString() || null,
                p_end_date: endDate?.toISOString() || null,
            });

            if (error) {
                console.error('Error fetching campaigns performance:', error);
                throw error;
            }

            return (data || []).map((item: any) => ({
                campagne: item.result_campagne,
                call_count: item.result_call_count,
                resolution_rate: item.result_resolution_rate,
                satisfaction_rate: item.result_satisfaction_rate,
                avg_duration: item.result_avg_duration,
                performance_rate: item.result_performance_rate,
                pain_points_rate: item.result_pain_points_rate,
                conversion_rate: item.result_conversion_rate,
                reiteration_rate: item.result_reiteration_rate
            })) as CampaignPerformanceData[];
        },
        enabled: !!projectId && enabled,
        staleTime: 5 * 60 * 1000, // 5 minutes
        gcTime: 30 * 60 * 1000,
        refetchOnWindowFocus: false,
    });
};
